import { useCallback, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import debounce from 'lodash/debounce'
import { UNITED_STATES } from '@/constants/regions'
import { useGBExperiment } from '@/experimentation'
import { isUnitedStatesOrCanada } from '@/services/UserRegionService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { UserLocation } from '@/utils/location'
import { logger } from '@/utils/logging'
import { useTranslate } from '@/utils/translate/translate-client'

// Baseline layers
const BASELINE_LAYERS = '-country,-region,-continent,-street,-intersection,-address,-neighbourhood,-borough'

// Experimental layers minus address, which may be added later only if the search term is likely an address,
// otherwise search results will include lots of addresses that are unlikely to be theater venues
const EXPERIMENTAL_LAYERS = 'country,region,county,localadmin,locality'

interface searchResult {
  geometry: {
    coordinates: [number, number]
  }
  properties: {
    label: string
    id: string
  }
}

export interface AddressSearchResult extends Omit<searchResult, 'geometry'> {
  geometry: {
    coordinates: {
      latitude: number
      longitude: number
    }
  }
}

const extractCoordinates = (geometry: searchResult['geometry']) => {
  return {
    coordinates: {
      latitude: geometry?.coordinates?.[1],
      longitude: geometry?.coordinates?.[0],
    },
  }
}

const formatSearchResults = (searchResults: searchResult[] | undefined) => {
  if (!searchResults) return []
  return searchResults.map((result) => {
    return { ...result, geometry: extractCoordinates(result.geometry) }
  })
}
interface SearchParams {
  term: string
  lang: string
  abort: AbortController
  countryCode: string
  coordinates?: UserLocation['coordinates']
  layers: string
}

// Heuristic to decide if a query looks like a street address
const isLikelyAddress = (input: string) => {
  const q = input?.trim() ?? ''
  if (!q) return false
  // Starts with a number (e.g., "123 Main")
  if (/^[0-9]+\s/.test(q)) return true
  // Contains unit indicators (e.g., "#5", "apt 2", "suite 300")
  if (/(^|\s)(#|apt|suite|ste|unit)\.?\s*\w*/i.test(q)) return true
  // Contains common street suffixes
  if (/\b(ave|avenue|st|street|rd|road|blvd|boulevard|ln|lane|dr|drive|ct|court|pkwy|parkway|hwy|highway)\b/i.test(q))
    return true
  return false
}

const autoCompleteSearch = async ({ term, abort, lang, coordinates, countryCode, layers }: SearchParams) => {
  if (!term) return []

  // Ensure we have a valid country code
  const effectiveCountryCode = countryCode || UNITED_STATES

  const params = {
    text: term,
    ['api_key']: process.env.NEXT_PUBLIC_TICKET_SEARCH_KEY,
    ['focus.point.lat']: coordinates?.latitude,
    ['focus.point.lon']: coordinates?.longitude,
    ['boundary.country']: effectiveCountryCode,
    layers,
    lang,
  }

  try {
    const { data } = await axios.get('https://api.geocode.earth/v1/autocomplete?', {
      params,
      signal: abort.signal,
    })
    return formatSearchResults(data?.features)
  } catch (error: unknown) {
    if (axios.isCancel(error)) return
    logger().warn('Search error in autoCompleteSearch function:', error)
  }
}

interface SearchByTermParams {
  term: string
  lang: string
  countryCode: string
  coordinates?: UserLocation['coordinates']
  layers: string
}

const searchByTerm = async ({ term, lang, coordinates, countryCode, layers }: SearchByTermParams) => {
  if (!term) return []

  const params = {
    text: term,
    ['api_key']: 'ge-16b18ebbc8994e32',
    ['focus.point.lat']: coordinates?.latitude,
    ['focus.point.lon']: coordinates?.longitude,
    ['boundary.country']: countryCode,
    layers,
    lang,
  }

  const { data } = await axios.get('https://api.geocode.earth/v1/search?', {
    params,
  })

  return formatSearchResults(data?.features)
}

const getLayers = (useExperimentalLayers: boolean, term: string) => {
  if (useExperimentalLayers) {
    return isLikelyAddress(term) ? `${EXPERIMENTAL_LAYERS},address` : EXPERIMENTAL_LAYERS
  }
  return BASELINE_LAYERS
}

interface Props {
  countryCode: string
  limitBy?: number
}

const useAutoCompleteAddressSearch = ({ countryCode, limitBy }: Props) => {
  const { t } = useTranslate('tickets')
  const { locale } = useLocale()
  const { userLocation } = useUser()
  const [results, setResults] = useState<AddressSearchResult[] | []>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef(new AbortController())
  const searchCountryCode = useMemo(() => {
    if (isUnitedStatesOrCanada(countryCode)) return 'US,CA'
    return countryCode
  }, [countryCode])
  const [useExperimentalLayers] = useGBExperiment('new_autocomplete_search_layers', false)

  const search = useMemo(() => {
    return debounce(async (term: string) => {
      try {
        if (!term) {
          return setResults([])
        }
        setLoading(true)
        setError(null)
        abortRef.current.abort()

        abortRef.current = new AbortController()

        const searchResults = await autoCompleteSearch({
          term,
          lang: locale,
          coordinates: userLocation?.coordinates,
          abort: abortRef.current,
          countryCode: searchCountryCode,
          layers: getLayers(useExperimentalLayers, term),
        })

        if (limitBy) {
          const results = searchResults ?? []
          setResults(results.slice(0, limitBy))
        } else {
          setResults(searchResults ?? [])
        }
      } catch (err: unknown) {
        if (axios.isCancel(err)) return
        const error = err as Error
        logger().warn('Failed to ticket search!', { coordinates: userLocation?.coordinates, searchCountryCode }, error)
        setError(error?.message ?? t('somethingWentWrongWhileSearching', 'Something went wrong while searching'))
      } finally {
        setLoading(false)
      }
    }, 500)
  }, [limitBy, locale, searchCountryCode, t, userLocation?.coordinates, useExperimentalLayers])

  const manualSearch = useCallback(
    async (term: string) => {
      try {
        setLoading(true)
        const results = await searchByTerm({
          term,
          lang: locale,
          coordinates: userLocation?.coordinates,
          countryCode: searchCountryCode,
          layers: getLayers(useExperimentalLayers, term),
        })

        setResults(results ?? [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logger().warn('Failed to do ticket search!', { coordinates: userLocation?.coordinates, searchCountryCode }, err)
        setError(err?.message ?? t('somethingWentWrongWhileSearching', 'Something went wrong while searching'))
      } finally {
        setLoading(false)
      }
    },
    [locale, searchCountryCode, t, userLocation?.coordinates, useExperimentalLayers],
  )

  const clearResults = useCallback(() => {
    setResults([])
  }, [])

  return useMemo(() => {
    return { search, manualSearch, results, clearResults, loading, error }
  }, [clearResults, error, loading, results, search, manualSearch])
}

export default useAutoCompleteAddressSearch
